<script>
import { preventDefault } from 'svelte/legacy';
import { onMount } from 'svelte';
import {
    authState
} from "@app/store/authStore.svelte.js";
import {
    modalState, hideModal, showModal
} from "@app/store/modalStore.svelte.js";
import {
    projectState
} from "@app/store/projectStore.svelte.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { markerState } from "@app/store/markerStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import { toast } from '@zerodevx/svelte-toast';
import { fade } from "svelte/transition";
import { BarLoader } from "svelte-loading-spinners";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import tcLib from "@cclib/lib/timecode.js";
import TcRangeSlider from "@app/components/inputs/TcRangeSlider.svelte";
/* Firebase */
import { auth, functions } from '@app/configs/firebase.js';
import db from '@app/configs/firestore.js';
import storage from '@app/configs/storage.js';
import { httpsCallable } from "firebase/functions";
import { collection, doc, setDoc, updateDoc, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, uploadString } from "firebase/storage";
/* Language Settings (transcription always uses Speechmatics) */
import speechmaticsLanguages from "@cclib/providers/speechmatics/languages.js";
import * as m from '@app/paraglide/messages.js';
/* Shared AI AD / SD config options */
import {
    OUTPUT_LANGUAGES,
    DESCRIPTION_LENGTH_PRESETS,
    DESCRIPTION_FOCUS_OPTIONS,
    DEFAULT_FOCUS,
    TRIAL_MAX_DURATION_SECONDS,
    clampSegment
} from "@app/lib/aiAd/options.js";
import { detectSceneChanges } from "@app/lib/detectSceneChanges.js";
import { extractShotFrames } from "@app/lib/aiAd/extractFrames.js";

/* FFmpeg */
const ffmpeg = window.ffmpeg;
const os = window.os;
const path = window.path;
const fs = window.fsSync;
const exec = window.exec;

/* Job config schema version. v3 is the simplified UI: the AI models, the
   transcription provider (Speechmatics) and duplicate avoidance are fixed
   server-side. */
const CONFIG_SCHEMA_VERSION = 3;

let sdDefaults = JSON.parse(localStorage.getItem("cc-scene-description-defaults")) || {};

/* Settings. Older saved presets may still hold removed fields (llmProvider,
   transcriptionProvider, avoidDuplicates, ...); they are simply ignored. */
/* Saved presets may hold a language code from another transcription provider */
let language = $state(speechmaticsLanguages[sdDefaults.language] ? sdDefaults.language : "en"),
    outputLanguage = $state(sdDefaults.outputLanguage ?? "en"),
    descriptionLength = $state(sdDefaults.descriptionLength ?? "standard"),
    descriptionFocus = $state(Array.isArray(sdDefaults.descriptionFocus) && sdDefaults.descriptionFocus.length > 0
        ? sdDefaults.descriptionFocus
        : [...DEFAULT_FOCUS]),
    contentDescription = $state(sdDefaults.contentDescription || ""),
    mainCharacters = $state(sdDefaults.mainCharacters || ""),
    additionalInstructions = $state(sdDefaults.additionalInstructions || ""),
    waiting = $state(false),
    statusMsg = $state(""),
    progress = $state(0);

/* Segment selection, in media-relative seconds. When `processEntireVideo` is on
   (the default) the range slider is hidden and the whole clip is processed. */
let processEntireVideo = $state(sdDefaults.processEntireVideo ?? true),
    segmentStartSec = $state(Number(sdDefaults.segmentStartSec) || 0),
    segmentEndSec = $state(Number(sdDefaults.segmentEndSec) || 0);

/* Section collapse state */
let openSceneSettings = $state(true),
    openContext = $state(false),
    openSegment = $state(false);

let userId = auth.currentUser.uid,
    userEmail = auth.currentUser.email,
    teamId = authState.team ? authState.team.id : null,
    teamOwner = authState.team ? authState.team.owner : null,
    teamOwnerId = authState.team ? authState.team.ownerId : null,
    homeRef = authState.team ? collection(db, "teams", teamId, "jobs") : collection(db, "users", userId, "jobs"),
    storageRef = ref(storage),
    uploadBasePath = authState.team ? "teams/" + teamId + "/jobs/sceneDescriptions/" : "users/" + userId + "/jobs/sceneDescriptions/";

/* Computed */
let videoDurationSeconds = $state(0);
/* Segment is invalid when the range is empty. While the video duration is still
   loading we leave it valid so the user isn't told it's broken prematurely. */
let segmentInvalid = $derived(
    !processEntireVideo
        && videoDurationSeconds > 0
        && !(segmentEndSec > segmentStartSec)
);
let processedSeconds = $derived(
    processEntireVideo
        ? videoDurationSeconds
        : clampSegment(segmentStartSec, segmentEndSec, videoDurationSeconds).duration
);
/* Billing is per started minute of the processed segment, minimum 3 minutes */
let durationMinutes = $derived(Math.ceil(processedSeconds / 60));
let estimatedCost = $derived(Math.max(3, durationMinutes));
let isTrial = $derived(authState.status === "in_trial");
let trialTooLong = $derived(isTrial && processedSeconds > TRIAL_MAX_DURATION_SECONDS);

/* Default (or repair) the out point once the video duration is known */
$effect(() => {
    if (videoDurationSeconds > 0 && (segmentEndSec <= 0 || segmentEndSec > videoDurationSeconds)) {
        segmentEndSec = videoDurationSeconds;
    }
    if (videoDurationSeconds > 0 && segmentStartSec >= videoDurationSeconds) {
        segmentStartSec = 0;
    }
});

/* Get video duration on mount */
onMount(() => {
    getVideoDuration();
});

async function getVideoDuration() {
    try {
        if (playerState.duration) {
            videoDurationSeconds = playerState.duration;
        } else {
            var matches;
            try {
                let ffmpegRes = await exec(
                    `${window.ffmpegPath.replace("app.asar", "app.asar.unpacked")} -y -i "${projectState.media.localPath}"`,
                );
                matches = ffmpegRes.match(
                    /(?<=duration\:\s)\d\d:\d\d:\d\d\.\d\d/im,
                );
            } catch (err) {
                matches = err.message.match(
                    /(?<=duration\:\s)\d\d:\d\d:\d\d\.\d\d/im,
                );
            }

            if (matches) {
                videoDurationSeconds = tcLib.tcMsToSec(matches[0]);
            }
        }
    } catch (err) {
        console.error("Error getting video duration:", err);
    }
}

function toggleFocus(id) {
    if (descriptionFocus.includes(id)) {
        descriptionFocus = descriptionFocus.filter(f => f !== id);
    } else {
        descriptionFocus = [...descriptionFocus, id];
    }
}

async function submitSceneDescriptionJob() {
    try {
        if (trialTooLong) {
            toast.push(m.sd_trial_too_long(), { classes: ["toast-warning"] });
            return;
        }

        if (segmentInvalid) {
            openSegment = true;
            toast.push(m.sd_segment_invalid(), { classes: ["toast-warning"] });
            return;
        }

        // Resolve segment selection. `clampSegment` snaps to [0, duration] and
        // guarantees `end >= start` so the ffmpeg / Firestore values stay sane.
        const seg = processEntireVideo
            ? clampSegment(0, videoDurationSeconds, videoDurationSeconds)
            : clampSegment(segmentStartSec, segmentEndSec, videoDurationSeconds);

        // Confirm cost
        let swalRes = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.confirm_scene_description_job_title(),
            text: m.confirm_scene_description_job_text({ cost: estimatedCost.toFixed(2) }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: m.yes(),
            cancelButtonText: m.cancel()
        });

        if (!swalRes.isConfirmed) {
            return;
        }

        waiting = true;
        progress = 0;
        statusMsg = m.extracting_audio();

        // Save user preferences
        localStorage.setItem("cc-scene-description-defaults", JSON.stringify({
            language,
            outputLanguage,
            descriptionLength,
            descriptionFocus,
            contentDescription,
            mainCharacters,
            additionalInstructions,
            processEntireVideo,
            segmentStartSec,
            segmentEndSec
        }));

        const jobId = uuidv4();
        const jobStoragePath = uploadBasePath + jobId + "/";
        const tempDir = os.tmpdir() + path.sep + "cc-sd-" + jobId;

        // Create temp directory
        if (!fs.existsSync(tempDir)) {
            fs.mkdirSync(tempDir, { recursive: true });
        }
        const framesDir = tempDir + path.sep + "frames";
        if (!fs.existsSync(framesDir)) {
            fs.mkdirSync(framesDir, { recursive: true });
        }

        // Step 1: Register job document in Firestore
        const jobDoc = {
            id: jobId,
            type: "automatic shot descriptions",
            status: "Submitted",
            progress: 0,
            statusMsg: "Extracting resources...",
            currentStep: "extraction",
            failedStep: null,
            failedError: null,
            projectId: projectState.id,
            projectName: projectState.name,
            submittedBy: userEmail,
            userId: userId,
            teamId: teamId,
            owner: teamOwner,
            ownerId: teamOwnerId,
            // Full source length; the cloud functions bill the processed segment.
            duration: videoDurationSeconds,
            cost: 0,
            chars: 0,
            deleted: false,
            archived: false,
            createdOn: Timestamp.fromDate(new Date()),
            updatedOn: Timestamp.fromDate(new Date()),
            completedOn: null,
            config: {
                configSchemaVersion: CONFIG_SCHEMA_VERSION,
                clientVersion: environment.version,
                // Fields read by Functions deployed before schema v3. They hold the
                // values v3 fixes server-side, so a new app submitting to an older
                // Functions deploy still transcribes with Speechmatics and uses Claude.
                transcriptionProvider: "Speechmatics",
                llmProvider: "Anthropic",
                backupLlmProvider: "Gemini",
                avoidDuplicates: true,
                dialogueSource: "transcription",
                language: language,
                outputLanguage: outputLanguage,
                descriptionLength: descriptionLength,
                descriptionFocus: descriptionFocus,
                segmentStart: seg.start,
                segmentEnd: seg.end,
                frameRate: projectState.frameRate,
                dropFrame: projectState.dropFrame,
                userContext: {
                    contentDescription: contentDescription,
                    mainCharacters: mainCharacters,
                    additionalInstructions: additionalInstructions
                }
            }
        };

        await setDoc(doc(homeRef, jobId), jobDoc);
        progress = 5;

        // Step 2: Extract audio (16kHz mono WAV) — restricted to the selected
        // segment so the cloud transcriber only processes that audio.
        statusMsg = m.extracting_audio();
        const audioOutputPath = tempDir + path.sep + "audio.wav";
        await extractAudio(projectState.media.localPath, audioOutputPath, seg.start, seg.duration);
        progress = 20;

        // Step 3: Detect scene changes. When the user pulled in markers from the
        // project, shift them into segment-relative time and drop any that fall
        // outside the segment so the shot list lines up with the audio / frames.
        statusMsg = m.detecting_scenes();
        let shots;

        const existingMarkers = markerState.lists[0]?.markers || [];
        if (existingMarkers.length > 0) {
            const shotSourceRes = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                title: m.ad_shot_source_title(),
                text: m.ad_shot_source_text({ count: existingMarkers.length }),
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: m.ad_shot_source_use_markers(),
                cancelButtonText: m.ad_shot_source_detect_auto()
            });

            if (shotSourceRes.isConfirmed) {
                shots = existingMarkers
                    .map(marker => ({ time: marker.time, score: 1 }))
                    .filter(s => s.time >= seg.start && s.time <= seg.end)
                    .map(s => ({ time: s.time - seg.start, score: s.score }))
                    .sort((a, b) => a.time - b.time);
            } else {
                shots = await detectSceneChanges({
                    inputPath: projectState.media.localPath,
                    startSec: seg.start,
                    durationSec: seg.duration,
                    sensitivity: "ai",
                });
            }
        } else {
            shots = await detectSceneChanges({
                inputPath: projectState.media.localPath,
                startSec: seg.start,
                durationSec: seg.duration,
                sensitivity: "ai",
            });
        }
        progress = 35;

        // Step 4: Extract thumbnails — about one every 2 seconds of each shot
        // (1-10 per shot), offset 3 frames from the shot boundaries.
        statusMsg = m.extracting_frames();
        const frameFiles = await extractShotFrames({
            ffmpeg,
            path,
            fs,
            inputPath: projectState.media.localPath,
            outputDir: framesDir,
            shots,
            segmentStart: seg.start,
            segmentDuration: seg.duration,
            frameRate: projectState.frameRate,
            onProgress: (completed, total) => {
                progress = 35 + Math.floor((completed / Math.max(1, total)) * 15);
            }
        });
        progress = 50;

        statusMsg = m.ad_extraction_complete();

        // Step 5: Upload audio
        statusMsg = m.uploading_audio();
        const audioBuffer = fs.readFileSync(audioOutputPath);
        await uploadBytes(ref(storageRef, jobStoragePath + "audio/audio.wav"), audioBuffer);
        progress = 60;

        // Step 6: Upload shots.json
        statusMsg = m.uploading_shots();
        await uploadString(ref(storageRef, jobStoragePath + "shots/shots.json"), JSON.stringify(shots));
        progress = 65;

        // Step 7: Upload frames
        const totalFrames = frameFiles.length;
        let uploadedFrames = 0;

        for (const frameFile of frameFiles) {
            statusMsg = m.uploading_frames({ completed: uploadedFrames, total: totalFrames });
            const framePath = framesDir + path.sep + frameFile;
            const frameBuffer = fs.readFileSync(framePath);
            await uploadBytes(ref(storageRef, jobStoragePath + "frames/" + frameFile), frameBuffer);
            uploadedFrames++;
            progress = 65 + Math.floor((uploadedFrames / totalFrames) * 25);
        }

        progress = 90;
        statusMsg = m.submitting_ad_job();

        // Step 8: Update job status and call cloud function
        await updateDoc(doc(homeRef, jobId), {
            status: "Processing",
            statusMsg: "Queued for processing",
            currentStep: "queued",
            progress: 20,
            updatedOn: Timestamp.fromDate(new Date())
        });

        await httpsCallable(functions, "v8SubmitSceneDescriptionJob")({ jobId, teamId });

        progress = 100;
        statusMsg = m.ad_upload_complete();

        toast.push(m.scene_description_job_submitted_success(), {
            classes: ["toast-success"]
        });

        // Clean up temp files
        try {
            fs.rmSync(tempDir, { recursive: true, force: true });
        } catch (cleanupErr) {
            console.warn("Failed to clean up temp directory:", cleanupErr);
        }

        showModal("aiSceneDescriptionImport");
    } catch (err) {
        console.error(err);
        waiting = false;
        toast.push(m.scene_description_job_failed(), { classes: ["toast-danger"] });
    }
}

/* Accepts optional `startSec` / `durationSec` so we can trim to the segment
   via `-ss` / `-t`. */
function extractAudio(inputPath, outputPath, startSec, durationSec) {
    return new Promise((resolve, reject) => {
        const cmd = ffmpeg(inputPath);
        if (startSec != null && startSec > 0) cmd.seekInput(startSec);
        if (durationSec != null && durationSec > 0) cmd.duration(durationSec);
        cmd.audioCodec('pcm_s16le')
            .audioChannels(1)
            .audioFrequency(16000)
            .output(outputPath)
            .on('end', resolve)
            .on('error', reject)
            .run();
    });
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'automaticSceneDescriptions' ? 'show d-block' : ''}"
    role="dialog"
    tabindex="-1"
    id="AutomaticSceneDescriptionsModal"
    aria-labelledby="automaticSceneDescriptionsTitle">
    <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="automaticSceneDescriptionsTitle">{m.automatic_scene_descriptions_job()}</h4>
                <button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p class="text-muted small">{m.automatic_scene_descriptions_description()}</p>

                {#if isTrial}
                    <div class="alert alert-info small mb-3" role="status">
                        <i class="bi bi-info-circle" aria-hidden="true"></i>
                        {m.sd_trial_banner()}
                    </div>
                {/if}

                <form onsubmit={preventDefault(submitSceneDescriptionJob)}>
                    <!-- 1. Scene Description Settings -->
                    <div class="card mb-2">
                        <div class="card-header bg-light" role="button" tabindex="0"
                             onclick={() => openSceneSettings = !openSceneSettings}
                             onkeydown={(e) => e.key === 'Enter' && (openSceneSettings = !openSceneSettings)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><i class="bi bi-camera-reels"></i> {m.ad_section_scene_description()}</h6>
                                <i class="bi bi-chevron-{openSceneSettings ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if openSceneSettings}
                            <div class="card-body" transition:fade={{ duration: 150 }}>
                                <div class="row">
                                    <div class="col-12 col-sm-6 mb-3">
                                        <label class="form-label" for="sdSourceLanguage">{m.source_language()}</label>
                                        <select class="form-select" id="sdSourceLanguage" bind:value={language} disabled={waiting}>
                                            {#each Object.entries(speechmaticsLanguages) as [code, name] (code)}
                                                <option value={code}>{name}</option>
                                            {/each}
                                        </select>
                                    </div>
                                    <div class="col-12 col-sm-6 mb-3">
                                        <label class="form-label" for="sdOutputLanguage">{m.ad_output_language_label()}</label>
                                        <select class="form-select" id="sdOutputLanguage" bind:value={outputLanguage} disabled={waiting}>
                                            {#each OUTPUT_LANGUAGES as opt (opt.code)}
                                                <option value={opt.code}>{opt.label}</option>
                                            {/each}
                                        </select>
                                    </div>
                                </div>
                                <small class="form-text text-muted d-block mb-3">{m.ad_output_language_help()}</small>

                                <div class="mb-3">
                                    <label class="form-label" for="sdDescriptionLength">{m.sd_description_length_label()}</label>
                                    <select class="form-select" id="sdDescriptionLength" bind:value={descriptionLength} disabled={waiting}>
                                        {#each Object.entries(DESCRIPTION_LENGTH_PRESETS) as [key, preset] (key)}
                                            <option value={key}>{preset.label}</option>
                                        {/each}
                                    </select>
                                    <small class="form-text text-muted">{m.sd_description_length_help()}</small>
                                </div>

                                <fieldset class="mb-0">
                                    <legend class="form-label fs-6">{m.ad_description_focus_label()}</legend>
                                    <div class="row g-2">
                                        {#each DESCRIPTION_FOCUS_OPTIONS as opt (opt.id)}
                                            <div class="col-12 col-sm-6">
                                                <div class="form-check">
                                                    <input class="form-check-input" type="checkbox"
                                                           id="sdFocus-{opt.id}"
                                                           checked={descriptionFocus.includes(opt.id)}
                                                           onchange={() => toggleFocus(opt.id)}
                                                           disabled={waiting} />
                                                    <label class="form-check-label" for="sdFocus-{opt.id}">{opt.label}</label>
                                                </div>
                                            </div>
                                        {/each}
                                    </div>
                                    <small class="form-text text-muted">{m.ad_description_focus_help()}</small>
                                </fieldset>
                            </div>
                        {/if}
                    </div>

                    <!-- 2. Context & Prompting -->
                    <div class="card mb-2">
                        <div class="card-header bg-light" role="button" tabindex="0"
                             onclick={() => openContext = !openContext}
                             onkeydown={(e) => e.key === 'Enter' && (openContext = !openContext)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><i class="bi bi-info-square"></i> {m.ad_section_context_prompting()}</h6>
                                <i class="bi bi-chevron-{openContext ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if openContext}
                            <div class="card-body" transition:fade={{ duration: 150 }}>
                                <div class="mb-3">
                                    <label class="form-label" for="sdContentDesc">{m.content_description_label()}</label>
                                    <textarea class="form-control" id="sdContentDesc" rows="2"
                                              bind:value={contentDescription}
                                              placeholder={m.content_description_placeholder()}
                                              disabled={waiting}></textarea>
                                </div>
                                <div class="mb-3">
                                    <label class="form-label" for="sdCharacters">{m.main_characters_label()}</label>
                                    <textarea class="form-control" id="sdCharacters" rows="2"
                                              bind:value={mainCharacters}
                                              placeholder={m.main_characters_placeholder()}
                                              disabled={waiting}></textarea>
                                </div>
                                <div class="mb-0">
                                    <label class="form-label" for="sdInstructions">{m.additional_instructions_label()}</label>
                                    <textarea class="form-control" id="sdInstructions" rows="2"
                                              bind:value={additionalInstructions}
                                              placeholder={m.additional_instructions_placeholder()}
                                              disabled={waiting}></textarea>
                                </div>
                            </div>
                        {/if}
                    </div>

                    <!-- 3. Segmentation -->
                    <div class="card mb-3">
                        <div class="card-header bg-light" role="button" tabindex="0"
                             onclick={() => openSegment = !openSegment}
                             onkeydown={(e) => e.key === 'Enter' && (openSegment = !openSegment)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><i class="bi bi-scissors"></i> {m.ad_section_segmentation()}</h6>
                                <i class="bi bi-chevron-{openSegment ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if openSegment}
                            <div class="card-body" transition:fade={{ duration: 150 }}>
                                <div class="form-check form-switch mb-2">
                                    <input class="form-check-input" type="checkbox" role="switch"
                                           id="sdProcessEntireVideo"
                                           bind:checked={processEntireVideo}
                                           disabled={waiting || videoDurationSeconds <= 0} />
                                    <label class="form-check-label" for="sdProcessEntireVideo">
                                        {m.sd_segment_process_entire_video()}
                                    </label>
                                </div>
                                <p class="small text-muted">
                                    {processEntireVideo
                                        ? m.sd_segment_help_entire_video()
                                        : m.sd_segment_help()}
                                </p>
                                {#if !processEntireVideo && videoDurationSeconds > 0}
                                    <TcRangeSlider bind:startSec={segmentStartSec}
                                                   bind:endSec={segmentEndSec}
                                                   durationSec={videoDurationSeconds}
                                                   disabled={waiting} />
                                    {#if segmentInvalid}
                                        <div class="alert alert-warning small mt-3 mb-0" role="alert">
                                            <i class="bi bi-exclamation-triangle"></i> {m.sd_segment_invalid()}
                                        </div>
                                    {/if}
                                {/if}
                            </div>
                        {/if}
                    </div>

                    <!-- Sticky footer area: trial gate, cost, submit -->
                    {#if trialTooLong}
                        <div class="alert alert-warning small" role="alert">
                            <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
                            {m.sd_trial_too_long()}
                        </div>
                    {/if}

                    <h6 class="text-warning">{m.cost_estimate()}</h6>
                    <div class="card mb-3">
                        <div class="card-body py-2">
                            <p class="mb-1"><strong>{m.ad_processed_duration_label()}:</strong> {durationMinutes > 0 ? m.duration_minutes({ minutes: durationMinutes }) : '...'}</p>
                            <p class="mb-1"><strong>{m.cost_per_minute()}</strong></p>
                            <p class="mb-1 text-muted small">{m.minimum_charge()}</p>
                            <p class="mb-0"><strong>{m.estimated_total({ cost: estimatedCost.toFixed(2) })}</strong></p>
                        </div>
                    </div>
                </form>

                <button class="btn btn-primary float-end" type="button"
                        disabled={waiting || videoDurationSeconds === 0 || trialTooLong || segmentInvalid}
                        onclick={submitSceneDescriptionJob}>
                    {m.submit_scene_description_job()}
                </button>

                {#if waiting}
                <div class="mt-3" role="status" aria-live="polite">
                    <BarLoader size="150" color="#1eb4b2" unit="px" duration="3s" />
                    <p class="small text-muted">
                        {statusMsg} ({progress}%)
                    </p>
                </div>
                {/if}
            </div>
        </div>
    </div>
</div>
